import React, { useState, useEffect, useRef } from 'react';
import {
  Loader2, X, ChevronDown, Image as ImageIcon, Video,
  AlertTriangle, Clock, XCircle,
} from 'lucide-react';
import { ActiveJob } from 'hooks/useActiveJobs';
import { apiPostCancelGeneration } from 'services/api/generationApi';

const OPERATION_LABELS: Record<string, string> = {
  inpaint: 'Inpainting',
  outpaint: 'Outpainting',
  translate: 'Translation',
  manual_translate: 'Manual Translation',
  migrate_format: 'Format Migration',
  reformat_margins: 'Reformat Margins',
  ai_crop: 'AI Crop',
  upscale: 'Upscale',
  downscale: 'Downscale',
  generate_video: 'Video Generation',
  edit_video: 'Video Edit',
  extend_video: 'Video Extension',
};

/* ── Elapsed timer ──────────────────────────────────────────────── */

const ElapsedTime: React.FC<{ since: any }> = ({ since }) => {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const ms = since?.toMillis?.() ?? 0;
  if (!ms) return null;
  const secs = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return (
    <span className="tabular-nums">
      {m}:{s.toString().padStart(2, '0')}
    </span>
  );
};

/* ── Progress bar ───────────────────────────────────────────────── */

const ProgressBar: React.FC<{ completed: number; total: number }> = ({ completed, total }) => {
  if (total <= 1) return null;
  const pct = Math.round((completed / total) * 100);
  return (
    <div className="flex items-center gap-2 mt-1.5">
      <div className="flex-1 h-1 bg-gray-700 rounded-full overflow-hidden">
        <div
          className="h-full bg-blue-500 rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[10px] text-gray-500 tabular-nums whitespace-nowrap">
        {completed}/{total}
      </span>
    </div>
  );
};

/* ── Source thumbnail ───────────────────────────────────────────── */

const SourceThumb: React.FC<{ url?: string; mediaKind: string; isStale: boolean }> = ({ url, mediaKind, isStale }) => {
  if (url) {
    return (
      <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0 border border-gray-700/50">
        <img src={url} alt="" className="w-full h-full object-cover" draggable={false} />
      </div>
    );
  }
  // Fallback icon
  return (
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
      isStale
        ? 'bg-amber-500/10 border border-amber-500/20'
        : mediaKind === 'videos'
        ? 'bg-purple-500/10 border border-purple-500/20'
        : 'bg-blue-500/10 border border-blue-500/20'
    }`}>
      {isStale ? (
        <AlertTriangle className="w-4 h-4 text-amber-400" />
      ) : mediaKind === 'videos' ? (
        <Video className="w-4 h-4 text-purple-400" />
      ) : (
        <ImageIcon className="w-4 h-4 text-blue-400" />
      )}
    </div>
  );
};

/* ── Main component ─────────────────────────────────────────────── */

interface ActiveJobsIndicatorProps {
  jobs: ActiveJob[];
  projectId: string;
  thumbnailMap?: Record<string, string>;
}

const ActiveJobsIndicator: React.FC<ActiveJobsIndicatorProps> = ({ jobs, projectId, thumbnailMap = {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [cancellingIds, setCancellingIds] = useState<Set<string>>(new Set());
  const [cancellingAll, setCancellingAll] = useState(false);
  const [expandedPrompts, setExpandedPrompts] = useState<Set<string>>(new Set());
  const prevCountRef = useRef(jobs.length);

  // Auto-open when a new job appears
  useEffect(() => {
    if (jobs.length > prevCountRef.current) {
      setIsOpen(true);
    }
    prevCountRef.current = jobs.length;
  }, [jobs.length]);

  if (jobs.length === 0) return null;

  const staleCount = jobs.filter((j) => j.isStale).length;

  const handleCancel = async (jobId: string) => {
    setCancellingIds((prev) => new Set(prev).add(jobId));
    try {
      await apiPostCancelGeneration(jobId, projectId);
    } catch (err) {
      console.error('Failed to cancel job:', err);
    }
    setCancellingIds((prev) => {
      const next = new Set(prev);
      next.delete(jobId);
      return next;
    });
  };

  const handleCancelAll = async () => {
    setCancellingAll(true);
    const pendingJobs = jobs.filter((j) => j.status === 'pending');
    await Promise.allSettled(pendingJobs.map((j) => apiPostCancelGeneration(j.job_id, projectId)));
    setCancellingAll(false);
  };

  const togglePrompt = (jobId: string) => {
    setExpandedPrompts((prev) => {
      const next = new Set(prev);
      next.has(jobId) ? next.delete(jobId) : next.add(jobId);
      return next;
    });
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
      {/* ── Expanded panel ────────────────────────────────────── */}
      <div
        className={`mb-2 bg-gray-900/95 backdrop-blur-sm border border-gray-700/80 rounded-2xl shadow-2xl w-96 overflow-hidden transition-all duration-300 origin-bottom ${
          isOpen
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-95 translate-y-2 pointer-events-none h-0 mb-0'
        }`}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-700/60 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
            <span className="text-sm font-medium text-gray-200">
              Active generations
            </span>
            <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded-full">
              {jobs.length}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {jobs.filter((j) => j.status === 'pending').length > 1 && (
              <button
                onClick={handleCancelAll}
                disabled={cancellingAll}
                className="text-xs text-gray-500 hover:text-red-400 px-2 py-1 rounded-lg hover:bg-red-400/10 transition-colors disabled:opacity-50"
              >
                Cancel all
              </button>
            )}
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Job list */}
        <ul className="max-h-80 overflow-y-auto divide-y divide-gray-800/60">
          {jobs.map((job) => {
            const isCancelling = cancellingIds.has(job.job_id);
            const promptText = job.prompts.filter(Boolean).join(', ');
            const isPromptExpanded = expandedPrompts.has(job.job_id);
            const thumbUrl = thumbnailMap[job.source_picture_id];

            return (
              <li
                key={job.job_id}
                className={`px-4 py-3 hover:bg-gray-800/30 transition-colors ${
                  isCancelling ? 'opacity-50' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Thumbnail */}
                  <SourceThumb url={thumbUrl} mediaKind={job.media_kind} isStale={job.isStale} />

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-200">
                        {OPERATION_LABELS[job.operation] || job.operation}
                      </p>
                      {job.status === 'pending' && (
                        <button
                          onClick={() => handleCancel(job.job_id)}
                          disabled={isCancelling}
                          className="flex-shrink-0 p-1 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-400/10 transition-colors disabled:opacity-50"
                          title="Cancel"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Status line */}
                    <div className="flex items-center gap-2 mt-0.5">
                      {job.isStale ? (
                        <span className="text-xs text-amber-400 font-medium">
                          Possibly failed
                        </span>
                      ) : (
                        <span className={`text-xs font-medium ${
                          job.status === 'running' ? 'text-green-400' : 'text-gray-500'
                        }`}>
                          {job.status === 'pending' ? 'Queued' : 'Running'}
                        </span>
                      )}
                      {/* Elapsed time */}
                      <span className="text-xs text-gray-600 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <ElapsedTime since={job.started_at || job.created_at} />
                      </span>
                    </div>

                    {/* Prompt preview */}
                    {promptText && (
                      <button
                        onClick={() => togglePrompt(job.job_id)}
                        className="mt-1.5 text-left w-full"
                      >
                        <p className={`text-xs text-gray-500 ${
                          isPromptExpanded ? '' : 'line-clamp-1'
                        }`}>
                          {promptText}
                        </p>
                      </button>
                    )}

                    {/* Progress bar for multi-variant */}
                    <ProgressBar completed={job.completed_count} total={job.total_count} />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* ── Tab button ────────────────────────────────────────── */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex items-center gap-2.5 px-5 py-3 rounded-2xl shadow-lg transition-all duration-200 hover:scale-105 active:scale-95 ${
          staleCount > 0
            ? 'bg-gradient-to-r from-amber-600 to-amber-500 text-white shadow-amber-500/20'
            : 'bg-gray-800/95 backdrop-blur-sm hover:bg-gray-700/95 border border-gray-600/50 text-gray-200 shadow-black/30'
        }`}
      >
        <div className="relative">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
        <span className="text-sm font-medium">
          {jobs.length} pending job{jobs.length > 1 ? 's' : ''}
        </span>
        {staleCount > 0 && (
          <AlertTriangle className="w-4 h-4" />
        )}
      </button>
    </div>
  );
};

export default ActiveJobsIndicator;
